import type { TemplateSetting } from "@/profitability/settings/types";
import type {
  ReportColumnMeaning,
  ReportColumnRole,
  ReportDatasetMeaning,
  ReportObject,
} from "./report-object";

export type ReportValue = string | number | boolean | null;
export type ReportRow = Record<string, ReportValue>;
export type ReportColumnType =
  | "text"
  | "number"
  | "currency"
  | "percent"
  | "date"
  | "boolean";

export interface ReportColumn extends ReportColumnMeaning {
  key: string;
  label?: string;
  type: ReportColumnType;
  role?: ReportColumnRole;
  /** The one text column that says why a row is flagged; blank when fine. */
  flag?: boolean;
  /** The dataset whose records this column's values name (a job's technician). */
  references?: string;
  /**
   * The key of the template input (uploaded file) this column's values come
   * from (`payroll_hours`), so a missing figure can say which file to add.
   */
  source?: string;
  /**
   * The header, in that input's file, of the cell the value is read from as
   * it is ("Labor Cost"). A worked-out column (labor plus burden) has a
   * `source` and no `source_column`.
   */
  source_column?: string;
}
export interface ReportDataset extends ReportDatasetMeaning {
  id: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  period_field?: string;
  rate_field?: string | null;
  target_field?: string | null;
  key_columns?: string[];
  /**
   * The columns a record's id is made of, as the API resolved them: the key
   * columns, else the name column (`Profitability::ReportDataset`). Sent with
   * a dataset that has an object.
   */
  record_key_columns?: string[];
  name_column?: string;
  /** The API sends `name` and `plural` already resolved. */
  object?: ReportObject;
  title?: string;
  /**
   * Whether the script cited the rows of the files each record came from
   * (`cite`), so a record can open the place in the file its figures were
   * read from (`GET …/:id/citations`).
   */
  cited?: boolean;
  /**
   * The columns any record has a figure for: how the script made it (a
   * cell, a sum of rows, a formula) and the file rows it came from.
   */
  traced?: string[];
  /**
   * Rolled-up figures that add up only what is known (`total_of`), by
   * record id then column key: how many records they are missing and what
   * those are ("jobs"). Absent when no figure is partial.
   */
  partial?: Record<string, Record<string, PartialFigure>>;
  /**
   * A dataset longer than the API keeps inline (`ReportDataset::DETAIL_ROWS`):
   * `GET …/output` sends it without rows and says how many it has, and
   * `useReportOutput` reads them from `GET …/rows` a page at a time.
   */
  detail?: boolean;
  row_count?: number;
}

/** One page of a detail dataset's rows (`GET …/rows`). */
export interface ReportRowsPage {
  rows: ReportRow[];
  total_rows: number;
  next_offset: number | null;
}

export interface PartialFigure {
  missing: number;
  of: string | null;
}

export const AGGREGATE = {
  count: "count",
  sum: "sum",
  average: "average",
  min: "min",
  max: "max",
} as const;
export type ReportAggregate = (typeof AGGREGATE)[keyof typeof AGGREGATE];
export interface ReportSort {
  by: "value" | "label";
  direction: "asc" | "desc";
}
export type ReportOperator = "eq" | "in" | "contains" | "gte" | "lte";
interface BlockBase {
  id: string;
  dataset: string;
  label?: string;
  title?: string;
  pane: string;
}
export interface ReportRowDetail {
  title: string;
  subtitle?: string;
  groups: { title: string; fields: string[] }[];
}
export type ReportBlock = BlockBase &
  (
    | {
        type: "table";
        columns: string[];
        detail?: ReportRowDetail;
        sort?: { by: string; direction: "asc" | "desc" };
        flagged?: boolean;
      }
    | {
        type: "bar" | "line" | "pie";
        x: string;
        y: string;
        aggregate: ReportAggregate;
        /** Other fields the same chart can be broken down by, besides `x`. */
        x_options?: string[];
        sort?: ReportSort;
        /** Points to show before the rest are summed into one "Other". */
        limit?: number;
      }
    | {
        type: "summary";
        field: string;
        aggregate: ReportAggregate;
        summary_key?: string;
        /** Headline figures lead the page; supporting ones share one strip. */
        emphasis?: "headline" | "supporting";
      }
  );
export type ReportControl = {
  id: string;
  dataset: string;
  field: string;
  label?: string;
} & ({ type: "group" } | { type: "filter"; operator: ReportOperator });
export interface ReportPaneDeclaration {
  id: string;
  title: string;
  surface?: "analysis" | "review" | "records";
  /** The record list this view explicitly opens. */
  dataset?: string;
}
export interface ReportOutput {
  reconciliation?: Partial<
    Record<"matched" | "excluded" | "unmatched" | "conflicting", number>
  >;
  version: 1;
  /** The tabs, in the order the script declared them. */
  panes: ReportPaneDeclaration[];
  datasets: ReportDataset[];
  blocks: ReportBlock[];
  controls: ReportControl[];
  /** The ISO 4217 code its money is in ("USD" when absent). */
  currency?: string | null;
  settings?: TemplateSetting[];
  /** The rules the script declared (`define_rule`), and the app's own. */
  rules?: ReportRuleDeclaration[];
}

export interface ReportRuleDeclaration {
  id: string;
  label: string;
  /** The dataset whose records it flags. */
  on: string;
  /** What it checks, in the owner's words. */
  checks?: string;
  /** Absent means on. */
  enabled?: boolean;
}
export interface ReportDataQuery {
  source_id: string;
  filters: {
    field: string;
    operator: ReportOperator;
    value: ReportValue | ReportValue[];
  }[];
  group_by: string[];
  aggregates?: { field: string; operation: ReportAggregate; as: string }[];
  /**
   * A chart with a limit, ordered by its value: the API orders the groups by
   * this aggregate and keeps the top ones, so the chart reads one page
   * rather than every group.
   */
  order?: { field: string; direction: "asc" | "desc" };
  top?: number;
}
export interface ReportDataPage {
  rows: ReportRow[];
  total_rows: number;
  next_offset: number | null;
  /** With `order`: how many groups there were before the top ones were kept. */
  total_groups?: number;
  /** With `top`: the rest summed, where that adds up; null otherwise. */
  other?: number | null;
  /** With `order`: the first grouping field's lowest and highest value. */
  group_range?: [string, string] | null;
}
export type ReportControlValues = Record<string, ReportValue | ReportValue[]>;
